import { defineStore } from 'pinia'
import { useStoreAuth } from '@/stores/storeAuth'
import { supabase } from '@/supabase'

// All review columns except blur_placeholder (reduces PostREST egress)
const REVIEW_COLS = 'id, user_id, title_id, slug, tmdb_id, tmdb_type, image_path, score_a, score_b, score_c, score_d, score_e, score_f, score_overall, title_title, sentiment, comments, highlight, lowlight, is_private, is_auto, is_editable, created_at, updated_at, when, reviewed_at, tags, review_type, tv_season, tv_episode, title_subtitle, watched_method, watched_service, watched_region, watched_on, is_deleted, image_transform, comment_count, club_id, visibility, status'

/**
 * Build a review select string, optionally including blur_placeholder.
 * @param {object} opts
 * @param {boolean} opts.includeBlur - include blur_placeholder column
 * @param {boolean} opts.inner - use profiles!inner join (for username filtering)
 * @param {boolean} opts.bare - return only review columns (no joins)
 */
export function reviewSelect(opts = {}) {
  let cols = REVIEW_COLS
  if (opts.includeBlur) cols += ', blur_placeholder'
  if (opts.bare) return cols
  const profileJoin = opts.inner ? 'profiles!inner(*)' : 'profiles(*)'
  return `${cols}, titles(*), ${profileJoin}, clubs(id, name, slug, cover_url)`
}

export function isBlurEnabled() {
  if (typeof window === 'undefined') return false
  return localStorage.getItem('spectra_blur_placeholder_enabled') === 'true'
}

export const useStoreReviews = defineStore('storeReviews', {
  persist: {
    // Only persist the reviews array - pagination state should be fresh each session
    pick: ['reviews', 'loaded'],
    // Add a version for cache invalidation if schema changes
    serializer: {
      serialize: JSON.stringify,
      deserialize: JSON.parse
    }
  },
  state: () => ({
    reviews: [],
    currentPage: 1,
    hasMoreReviews: true,
    isLoading: false,
    pageSize: 10,
    loaded: false,
    // Track when reviews were last fetched to know if cache is stale
    lastFetchedAt: null,
    // Infinite scroll pagination state
    totalCount: null,
    loadedPages: [],
    loadingPages: []
  }),
  actions: {
    init() {
      const storeAuth = useStoreAuth()
      if (!storeAuth.user) {
        console.log(
          'Skipping user reviews initialization - user not authenticated'
        )
        return
      }
      // Hydrate cached reviews with fresh related data if we have cached reviews
      if (this.reviews.length > 0) {
        this.hydrateReviewsRelatedData()
      }
      this.fetchUserReviews()
    },

    /**
     * Hydrate cached reviews with fresh related data (profiles, titles)
     * This ensures that after app restart, cached reviews get updated profile/title info
     */
    async hydrateReviewsRelatedData() {
      if (this.reviews.length === 0) return

      // Get unique review IDs that need hydration (missing profiles or stale data)
      const reviewsNeedingHydration = this.reviews.filter(
        (r) => !r.profiles || !r.profiles.username
      )

      if (reviewsNeedingHydration.length === 0) return

      const reviewIds = reviewsNeedingHydration.map((r) => r.id)

      try {
        // Fetch fresh data for reviews missing profile info
        const { data, error } = await supabase
          .from('reviews')
          .select(
            'id, profiles(*), titles(*), clubs(id, name, slug, cover_url)'
          )
          .in('id', reviewIds)

        if (error) {
          console.error('Error hydrating review data:', error)
          return
        }

        // Merge the fresh related data into cached reviews
        if (data && data.length > 0) {
          const freshDataMap = new Map(data.map((r) => [r.id, r]))

          this.reviews = this.reviews.map((review) => {
            const freshData = freshDataMap.get(review.id)
            if (freshData) {
              return {
                ...review,
                profiles: freshData.profiles || review.profiles,
                titles: freshData.titles || review.titles
              }
            }
            return review
          })
        }
      } catch (error) {
        console.error('Failed to hydrate reviews:', error)
      }
    },

    updateReviews(fetchedReviews) {
      if (!fetchedReviews) return

      // Create a map from the existing reviews array for quick lookup
      const reviewsMap = new Map(
        this.reviews.map((review) => [review.id, review])
      )

      // Iterate over the fetched reviews array
      fetchedReviews.forEach((fetchedReview) => {
        if (reviewsMap.has(fetchedReview.id)) {
          // Update the existing review, preserving related data if new data is missing
          const existing = reviewsMap.get(fetchedReview.id)

          const merged = {
            ...existing,
            ...fetchedReview,
            // Preserve profiles if new data doesn't have it
            profiles: fetchedReview.profiles || existing.profiles,
            // Preserve titles if new data doesn't have it
            titles: fetchedReview.titles || existing.titles
          }

          reviewsMap.set(fetchedReview.id, merged)
        } else {
          // Insert the new review
          reviewsMap.set(fetchedReview.id, fetchedReview)
        }
      })

      // Update the reviews state with the updated array
      const oldReviews = this.reviews
      this.reviews = Array.from(reviewsMap.values())
      this.lastFetchedAt = Date.now()
    },

    async fetchUserReviews() {
      const {
        data: { user },
        error: authError
      } = await supabase.auth.getUser()

      // Check if user is authenticated
      if (!user || authError) {
        console.log('Skipping fetchUserReviews - user not authenticated')
        this.loaded = true
        return
      }

      const { data, error } = await supabase
        .from('reviews')
        .select(reviewSelect({ includeBlur: isBlurEnabled() }))
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching user reviews:', error)
        this.loaded = true
        return
      }

      this.updateReviews(data)
      this.loaded = true
    },

    async fetchAllReviews() {
      const { data, error } = await supabase
        .from('reviews')
        .select(reviewSelect({ includeBlur: isBlurEnabled() }))
        .eq('status', 'published')
        .order('created_at', { ascending: false })

      if (error) {
        console.log('Error fetching all reviews', error)
        return
      }
      this.updateReviews(data)

      return
    },

    async fetchPublicReviews(page = 1) {
      this.isLoading = true
      const from = (page - 1) * this.pageSize
      const to = from + this.pageSize - 1

      const { data, error, count } = await supabase
        .from('reviews')
        .select(reviewSelect({ includeBlur: isBlurEnabled() }), {
          count: 'exact'
        })
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .range(from, to)

      if (error) {
        console.log('Error fetching public reviews', error)
        this.isLoading = false
        return
      }

      if (page === 1) {
        this.reviews = data || []
      } else {
        this.reviews = [...this.reviews, ...(data || [])]
      }

      this.currentPage = page
      this.hasMoreReviews = (data?.length || 0) === this.pageSize
      this.isLoading = false
    },

    async fetchReviewsByUser(userId, page = 1) {
      this.isLoading = true
      const from = (page - 1) * this.pageSize
      const to = from + this.pageSize - 1

      const { data, error, count } = await supabase
        .from('reviews')
        .select(reviewSelect({ includeBlur: isBlurEnabled() }), {
          count: 'exact'
        })
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .eq('user_id', userId)
        .range(from, to)

      if (page === 1) {
        this.reviews = data || []
      } else {
        this.reviews = [...this.reviews, ...(data || [])]
      }

      this.currentPage = page
      this.hasMoreReviews = (data?.length || 0) === this.pageSize
      this.isLoading = false
    },

    async fetchReviewsByUsername(username, page = 1) {
      this.isLoading = true
      const from = (page - 1) * this.pageSize
      const to = from + this.pageSize - 1
      const { data, error, count } = await supabase
        .from('reviews')
        .select(
          reviewSelect({ includeBlur: isBlurEnabled(), inner: true }),
          { count: 'exact' }
        )
        .eq('status', 'published')
        .eq('profiles.username', username)
        .order('created_at', { ascending: false })
        .range(from, to)

      if (page === 1) {
        this.reviews = data || []
      } else {
        this.reviews = [...this.reviews, ...(data || [])]
      }

      this.currentPage = page
      this.hasMoreReviews = (data?.length || 0) === this.pageSize
      this.isLoading = false
    },

    async fetchReviewPage(username, page, sortKey = 'date') {
      if (this.loadedPages.includes(page) || this.loadingPages.includes(page))
        return
      this.loadingPages.push(page)
      const from = (page - 1) * this.pageSize
      const to = from + this.pageSize - 1
      let query = supabase
        .from('reviews')
        .select(
          reviewSelect({ includeBlur: isBlurEnabled(), inner: true }),
          { count: 'exact' }
        )
        .eq('status', 'published')
        .eq('profiles.username', username)

      switch (sortKey) {
        case 'oldest':
          query = query.order('created_at', { ascending: true })
          break
        case 'top':
          query = query.order('score_overall', { ascending: false })
          break
        case 'low':
          query = query.order('score_overall', { ascending: true })
          break
        case 'alpha':
          query = query.order('title_title', { ascending: true })
          break
        default:
          query = query.order('created_at', { ascending: false })
      }

      const { data, error, count } = await query.range(from, to)

      this.loadingPages = this.loadingPages.filter((p) => p !== page)

      if (error) {
        console.error('Error fetching review page', page, error)
        return
      }

      if (count !== null && count !== undefined) {
        this.totalCount = count
      }
      this.updateReviews(data || [])
      this.loadedPages.push(page)
      this.hasMoreReviews =
        this.totalCount !== null
          ? this.loadedPages.length * this.pageSize < this.totalCount
          : (data?.length || 0) === this.pageSize
    },

    async fetchReviewsByTitle(title) {
      const { data, error } = await supabase
        .from('reviews')
        .select(reviewSelect({ includeBlur: isBlurEnabled() }))
        .eq('status', 'published')
        .eq('title', title)
        .order('created_at', { ascending: false })

      this.updateReviews(data)
    },

    async fetchReviewByTitleId(titleId, type) {
      const { data, error } = await supabase
        .from('reviews')
        .select(reviewSelect({ includeBlur: isBlurEnabled() }))
        .eq('status', 'published')
        .eq('tmdb_id', titleId)
        .eq('tmdb_type', type)
        .order('created_at', { ascending: false })

      if (error) {
        console.log('Error fetching reviews by title ID and type', error)
        return
      }

      if (!data || data.length === 0) {
        return
      }

      this.updateReviews(data)
    },

    async fetchReviewById(id) {
      const { data, error } = await supabase
        .from('reviews')
        .select(reviewSelect({ includeBlur: isBlurEnabled() }))
        .eq('status', 'published')
        .eq('id', id)
        .order('created_at', { ascending: false })
        .single()

      if (error) {
        console.error('[storeReviews] Error fetching review by ID:', error)
        return
      }

      if (data) {
        this.updateReviews([data])
      } else {
        console.warn('[storeReviews] No data returned from fetchReviewById', {
          reviewId: id
        })
      }
    },

    async deleteReview(reviewId) {
      try {
        const { error } = await supabase
          .from('reviews')
          .delete()
          .eq('id', reviewId)

        if (error) throw error
        // Remove from store too
        this.reviews = this.reviews.filter((review) => review.id !== reviewId)
        return true
      } catch (error) {
        // this.authError = error
      }
    },
    async createDraft(reviewData) {
      const { data, error } = await supabase.functions.invoke('reviews/draft', {
        body: {
          tmdb_id: reviewData.tmdb_id,
          tmdb_type: reviewData.tmdb_type,
          title_title: reviewData.title_title,
          review_type: reviewData.review_type,
        }
      })
      if (error) {
        const errBody = await new Response(error.context.body).json()
        throw errBody
      }
      return data
    },

    async updateDraft(id, reviewData) {
      const { data, error } = await supabase.functions.invoke(
        `reviews/${id}/draft-update`,
        { body: reviewData }
      )
      if (error) {
        const errBody = await new Response(error.context.body).json()
        throw errBody
      }
      return data
    },

    async publishDraft(id, reviewData) {
      const { data, error } = await supabase.functions.invoke(
        `reviews/${id}/publish`,
        { body: reviewData }
      )
      if (error) {
        const errBody = await new Response(error.context.body).json()
        throw errBody
      }

      // Track review creation in PostHog
      if (import.meta.env.VITE_APP_MODE === 'production') {
        import('@/composables/usePostHog').then(({ usePostHog }) => {
          const { track } = usePostHog()
          track('Review Created', {
            title_type: reviewData.tmdb_type,
            rating: reviewData.rating
          })
        })
      }

      return data
    },

    async findDraftForTitle(tmdbId, tmdbType) {
      const { data, error } = await supabase
        .from('reviews')
        .select(reviewSelect({ bare: true }))
        .eq('tmdb_id', tmdbId)
        .eq('tmdb_type', tmdbType)
        .eq('status', 'draft')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) {
        console.error('Error finding draft for title:', error)
        return null
      }
      return data
    },

    async deleteDraft(id) {
      const { data, error } = await supabase.functions.invoke(
        `reviews/${id}/draft`,
        { method: 'DELETE' }
      )
      if (error) {
        console.error('Failed to delete draft:', error)
      }
      return data
    },

    async addReview(newReview) {
      const review = { ...newReview }
      const { data, error } = await supabase.functions.invoke('reviews', {
        body: review
      })
      if (error) {
        const errBody = await new Response(error.context.body).json()
        throw errBody
      }

      // Track review creation in PostHog
      if (import.meta.env.VITE_APP_MODE === 'production') {
        import('@/composables/usePostHog').then(({ usePostHog }) => {
          const { track } = usePostHog()
          track('Review Created', {
            title_type: review.title?.media_type,
            rating: review.rating
          })
        })
      }

      return data
    },

    async updateReview(updatedReview) {
      const body = { ...updatedReview }

      const path = `reviews/${updatedReview.id}/update`
      const { data, error } = await supabase.functions.invoke(path, {
        body: body
      })
      if (error) {
        const errBody = await new Response(error.context.body).json()
        console.error('[storeReviews] updateReview API error', {
          error: errBody,
          reviewId: updatedReview.id
        })
        throw errBody
      }

      // Background reconciliation: refresh review data with full relations
      // without blocking the return. This ensures the store eventually has
      // the latest data including titles, profiles, etc.
      this.fetchReviewById(updatedReview.id)

      return data
    },

    /**
     * Upload a review image to storage
     * @param {string} reviewId - The review ID
     * @param {Blob} imageBlob - The image blob to upload
     * @param {string} variant - Image variant: 'plain', 'with_text', 'social_share', 'thumbnail'
     * @param {object} transformData - Optional transform data (panX, panY, zoom, rotation)
     * @returns {Promise<object>} - The uploaded image record with public_url
     */
    async uploadReviewImage(
      reviewId,
      imageBlob,
      variant = 'plain',
      transformData = null
    ) {
      const formData = new FormData()
      formData.append('imageFile', imageBlob, `review-${variant}.jpg`)
      formData.append('reviewId', reviewId)
      formData.append('variant', variant)
      if (transformData) {
        formData.append('transformData', JSON.stringify(transformData))
      }

      const { data, error } = await supabase.functions.invoke(
        'review_images/upload',
        { body: formData }
      )

      if (error) {
        console.error('Failed to upload review image:', error)
        throw error
      }

      return data
    },

    /**
     * Get review images for a review
     * @param {string} reviewId - The review ID
     * @returns {Promise<array>} - Array of image records with public URLs
     */
    async getReviewImages(reviewId) {
      const { data, error } = await supabase
        .from('review_images')
        .select('*')
        .eq('review_id', reviewId)
        .order('variant')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Failed to get review images:', error)
        return []
      }

      // Construct public URLs client-side
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      return (data || []).map((img) => ({
        ...img,
        public_url: `${supabaseUrl}/storage/v1/object/public/review-images/${img.storage_path}`
      }))
    },

    /**
     * Get the primary review image URL for a variant
     * @param {string} reviewId - The review ID
     * @param {string} variant - Image variant (default: 'plain')
     * @returns {Promise<string|null>} - The public URL or null
     */
    async getReviewImageUrl(reviewId, variant = 'plain') {
      const { data, error } = await supabase
        .from('review_images')
        .select('storage_path')
        .eq('review_id', reviewId)
        .eq('variant', variant)
        .eq('is_primary', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error || !data) {
        if (error) {
          console.error('Failed to get review image URL:', error)
        }
        return null
      }

      // Construct public URL client-side
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      return `${supabaseUrl}/storage/v1/object/public/review-images/${data.storage_path}`
    },

    /**
     * Fetch a review image as a Blob for downloading
     * @param {string} reviewId - The review ID
     * @param {string} variant - Image variant (default: 'plain')
     * @returns {Promise<Blob|null>} - The image blob or null
     */
    async fetchReviewImageBlob(reviewId, variant = 'plain') {
      try {
        // First get the public URL
        const url = await this.getReviewImageUrl(reviewId, variant)
        if (!url) {
          console.error('No URL found for review image:', reviewId, variant)
          return null
        }

        // Fetch the image as a blob
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`)
        }

        return await response.blob()
      } catch (error) {
        console.error('Failed to fetch review image blob:', error)
        return null
      }
    },

    async loadMoreReviews() {
      if (!this.hasMoreReviews || this.isLoading) return
      const nextPage = this.currentPage + 1
      // We'll need to determine which fetch method to use based on the current context
      // This will be handled by the component
    },

    resetPagination() {
      this.currentPage = 1
      this.hasMoreReviews = true
      this.reviews = []
      this.totalCount = null
      this.loadedPages = []
      this.loadingPages = []
    },

    // ==========================================================================
    // Reactions
    // ==========================================================================

    /**
     * React to a review with an emoji and/or text
     * @param {string} reviewId - The review ID
     * @param {object} reaction - { emoji?: string, content?: string }
     * @returns {Promise<object>} - The created message and conversation ID
     */
    async reactToReview(reviewId, { emoji, content }) {
      const { data, error } = await supabase.functions.invoke(
        `reviews/${reviewId}/react`,
        {
          body: { emoji, content }
        }
      )

      if (error) {
        console.error('Failed to react to review:', error)
        throw error
      }

      return data
    },

    /**
     * Get reactions for a review
     * @param {string} reviewId - The review ID
     * @returns {Promise<object>} - { reactions: [], total: number, user_reaction: object|null }
     */
    async getReviewReactions(reviewId) {
      const { data, error } = await supabase.functions.invoke(
        `reviews/${reviewId}/reactions`,
        { method: 'GET' }
      )

      if (error) {
        console.error('Failed to get review reactions:', error)
        return { reactions: [], total: 0, user_reaction: null }
      }

      return data
    },

    /**
     * Remove your reaction from a review
     * @param {string} reviewId - The review ID
     * @returns {Promise<boolean>} - Success status
     */
    async removeReaction(reviewId) {
      const { data, error } = await supabase.functions.invoke(
        `reviews/${reviewId}/react`,
        { method: 'DELETE' }
      )

      if (error) {
        console.error('Failed to remove reaction:', error)
        return false
      }

      return data?.success || false
    },

    // =========================================================================
    // Comment Methods
    // =========================================================================

    /**
     * Get comments for a review
     * @param {string} reviewId - The review ID
     * @param {object} options - Pagination options { limit, offset }
     * @returns {Promise<object>} - { comments: [], total: number, has_more: boolean }
     */
    async getReviewComments(reviewId, options = {}) {
      const params = new URLSearchParams()
      if (options.limit) params.set('limit', options.limit)
      if (options.offset) params.set('offset', options.offset)

      const queryString = params.toString()
      const url = `reviews/${reviewId}/comments${queryString ? `?${queryString}` : ''}`

      const { data, error } = await supabase.functions.invoke(url, {
        method: 'GET'
      })

      if (error) {
        console.error('Failed to get review comments:', error)
        return { comments: [], total: 0, has_more: false }
      }

      return data
    },

    /**
     * Add a comment to a review
     * @param {string} reviewId - The review ID
     * @param {string} content - The comment content
     * @returns {Promise<object|null>} - The created comment or null
     */
    async addComment(reviewId, content) {
      const { data, error } = await supabase.functions.invoke(
        `reviews/${reviewId}/comments`,
        {
          method: 'POST',
          body: { content }
        }
      )

      if (error) {
        console.error('Failed to add comment:', error)
        throw error
      }

      return data?.comment || null
    },

    /**
     * Delete a comment from a review
     * @param {string} reviewId - The review ID
     * @param {string} commentId - The comment ID
     * @returns {Promise<boolean>} - Success status
     */
    async deleteComment(reviewId, commentId) {
      const { data, error } = await supabase.functions.invoke(
        `reviews/${reviewId}/comments/${commentId}`,
        { method: 'DELETE' }
      )

      if (error) {
        console.error('Failed to delete comment:', error)
        return false
      }

      return data?.success || false
    },

    /**
     * Like a comment
     * @param {string} reviewId - The review ID
     * @param {string} commentId - The comment ID
     * @returns {Promise<number|null>} - The updated like count or null
     */
    async likeComment(reviewId, commentId) {
      const { data, error } = await supabase.functions.invoke(
        `reviews/${reviewId}/comments/${commentId}/like`,
        { method: 'POST' }
      )

      if (error) {
        console.error('Failed to like comment:', error)
        throw error
      }

      return data?.like_count ?? null
    },

    /**
     * Remove a like from a comment
     * @param {string} reviewId - The review ID
     * @param {string} commentId - The comment ID
     * @returns {Promise<number|null>} - The updated like count or null
     */
    async unlikeComment(reviewId, commentId) {
      const { data, error } = await supabase.functions.invoke(
        `reviews/${reviewId}/comments/${commentId}/like`,
        { method: 'DELETE' }
      )

      if (error) {
        console.error('Failed to unlike comment:', error)
        throw error
      }

      return data?.like_count ?? null
    }
  },
  getters: {
    getReviewById: (state) => {
      return (id) => {
        const review = state.reviews.find((review) => review.id === id)
        return review
      }
    },

    getReviewsByUser: (state) => {
      return (userId) => {
        return state.reviews
          .filter(
            (review) =>
              review !== undefined &&
              review.user_id === userId &&
              review.status !== 'draft'
          )
          .sort(
            (a, b) =>
              new Date(b.created_at) - new Date(a.created_at)
          )
      }
    },

    getReviewsByUsername: (state) => {
      return (username) => {
        return state.reviews
          .filter(
            (review) =>
              review !== undefined &&
              review?.profiles?.username === username &&
              review.status !== 'draft'
          )
          .sort(
            (a, b) =>
              new Date(b.created_at) - new Date(a.created_at)
          )
      }
    },

    getReviewsByUserForTitle: (state) => {
      return (userId, titleId) => {
        return state.reviews.filter(
          (review) =>
            review !== undefined &&
            review.user_id === userId &&
            review.tmdb_id == titleId &&
            review.status !== 'draft'
        )
      }
    },

    getReviewsByTitleId: (state) => {
      return (id) =>
        state.reviews.filter(
          (review) =>
            review !== undefined &&
            review.tmdb_id == id &&
            review.status !== 'draft'
        )
    }
  }
})
